import React from 'react';
import { C } from '../constants';
import { fluxaiAlexUrl } from '../services/fluxai';
import { getToken, currentClaims } from '../services/auth';
import { chatearAlex } from '../services/fichas';

export default function AlexLauncher() {
  const [abierto, setAbierto] = React.useState(false);
  const [entrada, setEntrada] = React.useState('');
  const [historial, setHistorial] = React.useState([]);
  const [cargando, setCargando] = React.useState(false);
  const finRef = React.useRef(null);
  const claims = currentClaims();
  const sso = !!getToken();

  React.useEffect(() => {
    const open = () => setAbierto(true);
    window.addEventListener('alex:open', open);
    return () => window.removeEventListener('alex:open', open);
  }, []);

  React.useEffect(() => {
    if (!abierto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto]);

  React.useEffect(() => {
    if (finRef.current) finRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [historial, cargando]);

  const enviar = async () => {
    const q = entrada.trim();
    if (!q || cargando) return;
    setEntrada('');
    const hilo = [...historial, { role: 'user', content: q }];
    setHistorial(hilo);
    setCargando(true);
    try {
      const r = await chatearAlex(q, hilo.slice(0, -1), {
        nombre: claims?.name || claims?.email,
        sso: getToken() || undefined,
      });
      const texto = r?.respuesta || r?.texto || 'No pude responder ahora. Prueba de nuevo o escribe a WhatsApp 316 308 5286.';
      setHistorial([...hilo, { role: 'assistant', content: texto }]);
    } catch (e) {
      setHistorial([...hilo, { role: 'assistant', content: e?.message || 'Alex no está disponible en este momento.' }]);
    } finally {
      setCargando(false);
    }
  };

  const abrirFlux = () => {
    try { window.open(fluxaiAlexUrl({ embed: false }) || fluxaiAlexUrl(), '_blank', 'noopener,noreferrer'); } catch { /* */ }
  };

  return (
    <>
      {!abierto && (
        <button
          type="button"
          onClick={() => setAbierto(true)}
          title="Alex — soporte Solar Hub y FluxAI"
          aria-label="Abrir el asistente Alex"
          style={{
            position: 'fixed', right: 18, bottom: 46, zIndex: 60,
            width: 52, height: 52, borderRadius: 999,
            border: 'none', cursor: 'pointer',
            background: C.teal, color: C.heading,
            boxShadow: '0 8px 22px rgba(0,0,0,0.28)',
            display: 'grid', placeItems: 'center',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="8" width="18" height="12" rx="2" />
            <path d="M12 8V4M8 4h8" />
            <circle cx="8.5" cy="14" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="15.5" cy="14" r="1.2" fill="currentColor" stroke="none" />
          </svg>
        </button>
      )}

      {abierto && (
        <>
          <div onClick={() => setAbierto(false)} style={{ position: 'fixed', inset: 0, zIndex: 59, background: 'rgba(0,0,0,0.35)' }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 60,
            width: 'min(420px, 100vw)', display: 'flex', flexDirection: 'column',
            background: C.card, borderLeft: `1px solid ${C.border}`, boxShadow: '-8px 0 30px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.teal, color: C.heading }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800 }}>Alex · un cerebro</div>
                <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 600 }}>Solar Hub · FluxAI · chat de soporte{sso ? ' · sesión' : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" onClick={abrirFlux} title="Abrir en FluxAI" style={{ fontSize: 11, fontWeight: 700, color: C.heading, background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 7, padding: '5px 9px', cursor: 'pointer' }}>FluxAI ↗</button>
                <button type="button" onClick={() => setAbierto(false)} aria-label="Cerrar" style={{ fontSize: 16, lineHeight: 1, color: C.heading, background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 7, padding: '4px 9px', cursor: 'pointer' }}>×</button>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, background: C.dark }}>
              {historial.length === 0 && (
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>
                  Soy Alex. Te ayudo con el estudio, las fichas de la tienda, el kit de cierre y FluxAI (telemetría, PQR). También por WhatsApp al 316 308 5286.
                </div>
              )}
              {historial.map((m, i) => (
                <div key={i} style={{
                  marginBottom: 10, padding: '8px 10px', borderRadius: 10, fontSize: 13, lineHeight: 1.45,
                  background: m.role === 'user' ? `${C.yellow}18` : C.card,
                  color: C.heading, border: `1px solid ${C.border}`,
                  marginLeft: m.role === 'user' ? 28 : 0, marginRight: m.role === 'assistant' ? 28 : 0,
                }}>
                  {m.content}
                </div>
              ))}
              {cargando && <div style={{ fontSize: 12, color: C.muted }}>Alex está escribiendo…</div>}
              <div ref={finRef} />
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); enviar(); }}
              style={{ display: 'flex', gap: 8, padding: 10, borderTop: `1px solid ${C.border}`, background: C.card }}
            >
              <input
                value={entrada}
                onChange={(e) => setEntrada(e.target.value)}
                placeholder="Escribe a Alex…"
                aria-label="Mensaje para Alex"
                style={{ flex: 1, background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 11px', color: C.text, fontSize: 13 }}
              />
              <button type="submit" disabled={cargando || !entrada.trim()} style={{
                background: C.yellow, color: '#1a1205', border: 'none', borderRadius: 8,
                padding: '9px 12px', fontWeight: 800, fontSize: 12, cursor: cargando ? 'default' : 'pointer',
              }}>
                Enviar
              </button>
            </form>
          </div>
        </>
      )}
    </>
  );
}
